import type { BookingSummary, ExternalCalendarBlockSummary } from "@apartos/contracts";

const providerLabel = (provider: ExternalCalendarBlockSummary["provider"]) =>
  provider === "booking" ? "Booking.com" : provider === "airbnb" ? "Airbnb" : "Внешний канал";

export function externalCalendarBlocksAsBookings(blocks: ExternalCalendarBlockSummary[]): BookingSummary[] {
  return blocks.map((block) => ({
    id: `ical:${block.id}`,
    guestId: "",
    guestName: `${providerLabel(block.provider)} · занято`,
    guestInitials: block.provider === "booking" ? "B" : block.provider === "airbnb" ? "A" : "E",
    phone: "",
    apartmentNumber: block.apartmentNumber,
    source: block.provider === "airbnb" ? "airbnb" : block.provider === "booking" ? "booking" : "website",
    // Оплата по полоске канала неизвестна, поэтому она выглядит как «Ждёт заселения» без отметки об оплате.
    status: "pending_payment",
    checkInAt: `${block.startsOn}T14:00:00+05:00`,
    checkOutAt: `${block.endsOn}T12:00:00+05:00`,
    guests: 0,
    nightlyRate: 0,
    total: 0,
    paid: 0,
    currency: "USD",
    externalCalendar: { importFeedId: block.importFeedId, provider: block.provider, name: block.calendarName },
  }));
}

const activeStatuses = ["draft", "pending_payment", "confirmed", "checked_in"];

const overlapNights = (aStart: string, aEnd: string, bStart: string, bEnd: string) =>
  Math.max(0, Math.min(Date.parse(aEnd), Date.parse(bEnd)) - Math.max(Date.parse(aStart), Date.parse(bStart)));

// Блок Booking.com скрывается, когда по нему уже оформлена бронь в CRM (источник Booking, тот же апартамент,
// даты пересекаются): в календаре остаётся одна полоска — с именем гостя. Одна бронь CRM закрывает только один
// блок (сначала с теми же датами, иначе с наибольшим пересечением), чтобы второй блок на те же даты — овербукинг
// в Booking — оставался виден.
export function hideBlocksCoveredByBookings(blocks: ExternalCalendarBlockSummary[], bookings: BookingSummary[]): ExternalCalendarBlockSummary[] {
  const covered = new Set<string>();
  for (const booking of bookings) {
    if (booking.source !== "booking" || !activeStatuses.includes(booking.status)) continue;
    const checkIn = booking.checkInAt.slice(0, 10);
    const checkOut = booking.checkOutAt.slice(0, 10);
    let best: ExternalCalendarBlockSummary | undefined;
    let bestScore = 0;
    for (const block of blocks) {
      if (block.provider !== "booking" || covered.has(block.id) || block.apartmentNumber !== booking.apartmentNumber) continue;
      const overlap = overlapNights(checkIn, checkOut, block.startsOn, block.endsOn);
      if (overlap <= 0) continue;
      const score = block.startsOn === checkIn && block.endsOn === checkOut ? Number.MAX_SAFE_INTEGER : overlap;
      if (score > bestScore) { best = block; bestScore = score; }
    }
    if (best) covered.add(best.id);
  }
  return blocks.filter((block) => !covered.has(block.id));
}

export function externalStayNights(startsOn: string, endsOn: string): number {
  return Math.max(1, Math.round((Date.parse(`${endsOn}T12:00:00Z`) - Date.parse(`${startsOn}T12:00:00Z`)) / 86_400_000));
}

export function externalStayLabel(booking: Pick<BookingSummary, "checkInAt" | "checkOutAt">): string {
  const startsOn = booking.checkInAt.slice(0, 10);
  const endsOn = booking.checkOutAt.slice(0, 10);
  const format = (value: string) => new Date(`${value}T12:00:00Z`).toLocaleDateString("ru-RU", { day: "numeric", month: "short", timeZone: "UTC" });
  const nights = externalStayNights(startsOn, endsOn);
  return `${format(startsOn)} – ${format(endsOn)} · ${nights} ${nights === 1 ? "ночь" : nights < 5 ? "ночи" : "ночей"}`;
}
